import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  TrendingUp,
  Dumbbell,
  Clock,
  Plus,
  Trash2,
  Save,
} from "lucide-react";
import type { ProgressLog } from "@/types";

interface WorkoutFormData {
  exerciseName: string;
  sets: { reps: number; weight: number; rpe?: number }[];
}

export default function Progress() {
  const { progressLogs, refreshProgress, plan } = useAuth();
  const [activeTab, setActiveTab] = useState("log");
  const [workoutDate, setWorkoutDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [duration, setDuration] = useState("");
  const [notes, setNotes] = useState("");
  const [exercises, setExercises] = useState<WorkoutFormData[]>([
    { exerciseName: "", sets: [{ reps: 0, weight: 0 }] },
  ]);

  const addExercise = () => {
    setExercises([
      ...exercises,
      { exerciseName: "", sets: [{ reps: 0, weight: 0 }] },
    ]);
  };

  const addSet = (exerciseIndex: number) => {
    const newExercises = [...exercises];
    newExercises[exerciseIndex].sets.push({ reps: 0, weight: 0 });
    setExercises(newExercises);
  };

  const updateExerciseName = (index: number, name: string) => {
    const newExercises = [...exercises];
    newExercises[index].exerciseName = name;
    setExercises(newExercises);
  };

  const updateSet = (
    exerciseIndex: number,
    setIndex: number,
    field: "reps" | "weight" | "rpe",
    value: number
  ) => {
    const newExercises = [...exercises];
    newExercises[exerciseIndex].sets[setIndex][field] = value;
    setExercises(newExercises);
  };

  const removeExercise = (index: number) => {
    setExercises(exercises.filter((_, i) => i !== index));
  };

  const removeSet = (exerciseIndex: number, setIndex: number) => {
    const newExercises = [...exercises];
    newExercises[exerciseIndex].sets = newExercises[exerciseIndex].sets.filter(
      (_, i) => i !== setIndex
    );
    setExercises(newExercises);
  };

  const handleSave = async () => {
    // TODO: Implement saving workout log
    console.log({ workoutDate, duration, notes, exercises });
    setActiveTab("history");
  };

  // Get today's workout from plan
  const today = new Date().toLocaleDateString("en-US", { weekday: "long" });
  const todayWorkout = plan?.weeklySchedule.find((day) => day.day === today);

  return (
    <div className="min-h-screen pt-24 pb-12 px-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Progress Tracking</h1>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8">
            <TabsTrigger value="log" className="gap-2">
              <Plus className="w-4 h-4" />
              Log Workout
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <Calendar className="w-4 h-4" />
              History
            </TabsTrigger>
            <TabsTrigger value="stats" className="gap-2">
              <TrendingUp className="w-4 h-4" />
              Stats
            </TabsTrigger>
          </TabsList>

          <TabsContent value="log">
            <Card className="p-6">
              {/* Quick Add from Plan */}
              {todayWorkout && (
                <div className="mb-6 p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Dumbbell className="w-4 h-4 text-amber-500" />
                    Today's Scheduled Workout: {todayWorkout.focus}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {todayWorkout.exercises.slice(0, 4).map((ex) => (
                      <Badge
                        key={ex.name}
                        variant="outline"
                        className="cursor-pointer hover:bg-amber-500/10"
                        onClick={() => {
                          setExercises([
                            ...exercises,
                            { exerciseName: ex.name, sets: [{ reps: 0, weight: 0 }] },
                          ]);
                        }}
                      >
                        + {ex.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Workout Date */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="space-y-2">
                  <Label>Workout Date</Label>
                  <Input
                    type="date"
                    value={workoutDate}
                    onChange={(e) => setWorkoutDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Duration (minutes)
                  </Label>
                  <Input
                    type="number"
                    placeholder="60"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                  />
                </div>
              </div>

              {/* Exercises */}
              <div className="space-y-6">
                {exercises.map((exercise, exerciseIndex) => (
                  <div
                    key={exerciseIndex}
                    className="p-4 border border-border rounded-lg space-y-4"
                  >
                    <div className="flex items-center gap-4">
                      <Input
                        placeholder="Exercise name"
                        value={exercise.exerciseName}
                        onChange={(e) =>
                          updateExerciseName(exerciseIndex, e.target.value)
                        }
                        className="flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeExercise(exerciseIndex)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>

                    {/* Sets */}
                    <div className="space-y-2">
                      {exercise.sets.map((set, setIndex) => (
                        <div
                          key={setIndex}
                          className="flex items-center gap-4"
                        >
                          <span className="text-sm text-muted-foreground w-12">
                            Set {setIndex + 1}
                          </span>
                          <Input
                            type="number"
                            placeholder="Weight (lbs/kg)"
                            value={set.weight || ""}
                            onChange={(e) =>
                              updateSet(
                                exerciseIndex,
                                setIndex,
                                "weight",
                                parseFloat(e.target.value) || 0
                              )
                            }
                            className="w-32"
                          />
                          <span className="text-muted-foreground">×</span>
                          <Input
                            type="number"
                            placeholder="Reps"
                            value={set.reps || ""}
                            onChange={(e) =>
                              updateSet(
                                exerciseIndex,
                                setIndex,
                                "reps",
                                parseInt(e.target.value) || 0
                              )
                            }
                            className="w-24"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeSet(exerciseIndex, setIndex)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addSet(exerciseIndex)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Set
                    </Button>
                  </div>
                ))}
              </div>

              <div className="flex gap-4 mt-6">
                <Button variant="outline" onClick={addExercise}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Exercise
                </Button>
              </div>

              {/* Notes */}
              <div className="mt-6 space-y-2">
                <Label>Notes</Label>
                <textarea
                  className="w-full min-h-[100px] p-3 rounded-md border border-input bg-background text-sm"
                  placeholder="How did the workout feel? Any observations..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              <Button
                className="w-full mt-6 gap-2 bg-amber-500 text-black hover:bg-lime-500"
                onClick={handleSave}
              >
                <Save className="w-4 h-4" />
                Save Workout
              </Button>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            {progressLogs.length === 0 ? (
              <Card className="p-8 text-center">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">No Workouts Logged</h3>
                <p className="text-muted-foreground mb-4">
                  Start logging your workouts to track your progress over time.
                </p>
                <Button onClick={() => setActiveTab("log")}>Log First Workout</Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {progressLogs.map((log) => (
                  <Card key={log.id} className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-semibold">
                          {new Date(log.workoutDate).toLocaleDateString("en-US", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </h3>
                        {log.duration && (
                          <p className="text-sm text-muted-foreground">
                            {log.duration} minutes
                          </p>
                        )}
                      </div>
                      <Badge variant="secondary">
                        {log.exercises.length} exercises
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {log.exercises.map((ex) => (
                        <div
                          key={ex.name}
                          className="flex items-center justify-between text-sm"
                        >
                          <span>{ex.name}</span>
                          <span className="text-muted-foreground">
                            {ex.sets.length} sets
                          </span>
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="stats">
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="p-6 text-center">
                <Dumbbell className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-3xl font-bold">{progressLogs.length}</p>
                <p className="text-sm text-muted-foreground">Total Workouts</p>
              </Card>
              <Card className="p-6 text-center">
                <Calendar className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-3xl font-bold">
                  {progressLogs.filter((log) => {
                    const logDate = new Date(log.workoutDate);
                    const now = new Date();
                    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                    return logDate >= weekAgo;
                  }).length}
                </p>
                <p className="text-sm text-muted-foreground">This Week</p>
              </Card>
              <Card className="p-6 text-center">
                <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-3xl font-bold">
                  {Math.round(
                    progressLogs.reduce((acc, log) => acc + (log.duration || 0), 0) / 60
                  )}h
                </p>
                <p className="text-sm text-muted-foreground">Total Time</p>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
